import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, KeyRound, LogIn, PackageSearch, ShoppingCart, WifiOff, Wrench } from 'lucide-react';
import { Button, Input } from '../../components/ui';
import styles from './LoginView.module.css';

const FEATURES = [
  { icon: PackageSearch, text: 'Ersatzteile in Sekunden finden – nach Gerät, Name oder Artikelnummer' },
  { icon: ShoppingCart, text: 'Direkt bestellen, mit geprüfter Ticketnummer' },
  { icon: WifiOff, text: 'Auch ohne Netz nutzbar – Daten und Bilder liegen auf dem Gerät' }
];

function Brand({ compact = false }) {
  return (
    <div className={compact ? styles.brandCompact : styles.brand}>
      <span className={styles.logo} aria-hidden><Wrench /></span>
      <span>
        <strong className={styles.brandName}>Material-App</strong>
        <span className={styles.brandSub}>Ersatzteillogistik &amp; Dokumentation</span>
      </span>
    </div>
  );
}

/** Anmeldung im neuen Design – Logik liegt in LoginPage */
export default function LoginView({
  checkingAuth, isOnline, username, setUsername, password, setPassword, loading, error, syncProgress,
  onSubmit, onForgotPassword, version, passkeyAvailable, passkeyLoading, onPasskeyLogin
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className={styles.page}>
      <aside className={styles.hero} aria-hidden="true">
        <Brand />
        <div className={styles.heroText}>
          <h2>Alles für den Einsatz beim Kunden – in einer App.</h2>
          <ul className={styles.features}>
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text}><span className={styles.featureIcon}><Icon /></span>{text}</li>
            ))}
          </ul>
        </div>
        <span className={styles.heroFoot}>© {new Date().getFullYear()} Lars Jänsch</span>
      </aside>

      <main className={styles.main}>
        <div className={styles.panel}>
          <div className={styles.mobileBrand}><Brand compact /></div>

          {checkingAuth ? (
            <p className={styles.muted} role="status">Anmeldung wird geprüft …</p>
          ) : !isOnline ? (
            <section className={styles.offline} aria-labelledby="offline-title">
              <span className={styles.offlineIcon}><WifiOff aria-hidden /></span>
              <h1 id="offline-title" className={styles.title}>Keine Internetverbindung</h1>
              <p className={styles.muted}>Zum Anmelden wird einmalig eine Verbindung benötigt – deine Zugangsdaten werden auf dem Server geprüft.</p>
              <ul className={styles.list}>
                <li>Nach der Anmeldung bleibt die Sitzung <strong>7 Tage</strong> gültig.</li>
                <li>In dieser Zeit funktioniert die App auch <strong>ohne Internet</strong>.</li>
                <li>Sobald du wieder online bist, lädt diese Seite automatisch neu.</li>
              </ul>
            </section>
          ) : (
            <>
              <h1 className={styles.title}>Anmelden</h1>
              <p className={styles.muted}>Melde dich mit deiner Firmen-E-Mail an.</p>

              {error && (
                <div className={styles.error} role="alert">
                  <AlertCircle aria-hidden />
                  <span>{error}</span>
                </div>
              )}

              <form className={styles.form} onSubmit={onSubmit} noValidate>
                <Input
                  label="E-Mail"
                  type="email"
                  inputMode="email"
                  autoComplete="username webauthn"
                  placeholder="email@beispiel.de"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
                <div className={styles.passwordField}>
                  <Input
                    label="Passwort"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className={styles.reveal}
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                  </button>
                </div>

                <Button type="submit" size="lg" fullWidth icon={LogIn} loading={loading}>
                  {loading ? (syncProgress ? 'Daten werden geladen …' : 'Anmeldung läuft …') : 'Anmelden'}
                </Button>

                {loading && syncProgress && (
                  <div className={styles.progress} role="status" aria-live="polite">
                    <span className={styles.bar}><span style={{ width: `${Math.max(5, syncProgress.percent)}%` }} /></span>
                    <span className={styles.progressText}>{syncProgress.message}</span>
                  </div>
                )}

                <button type="button" className={styles.link} onClick={onForgotPassword} disabled={loading}>
                  Passwort vergessen?
                </button>
              </form>

              {passkeyAvailable && (
                <>
                  <div className={styles.divider}><span>oder</span></div>
                  <Button
                    variant="secondary"
                    size="lg"
                    fullWidth
                    icon={KeyRound}
                    loading={passkeyLoading}
                    disabled={loading}
                    onClick={onPasskeyLogin}
                  >
                    {passkeyLoading ? 'Warte auf Passkey …' : 'Mit Passkey anmelden'}
                  </Button>
                </>
              )}

              <p className={styles.hint}>Die Sitzung bleibt 7 Tage gültig – auch offline.</p>
            </>
          )}
        </div>

        <footer className={styles.footer}>
          <a href="https://bentomax.de/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
          <Link to="/datenschutz">Datenschutz</Link>
          {version && <span>v{version}</span>}
        </footer>
      </main>
    </div>
  );
}
